import { Permission } from '@procureai/permissions';
import { usePermissions } from '@procureai/permissions-ui';
import { useDebounce } from '@procureai/ui-mui';
import { keys, pickBy, pipe } from 'ramda';
import { useCallback, useMemo, useState } from 'react';

import { SvbrgCategoryType } from '@/generated/gql/graphql';

type CategoryPermissions = Record<SvbrgCategoryType, Permission>;

const threadCategoryPermissionMap: CategoryPermissions = {
  [SvbrgCategoryType.INTAKE]: Permission.IntakeList,
  [SvbrgCategoryType.SOURCING]: Permission.ConversationDemandUse,
  [SvbrgCategoryType.CONVERSATION]: Permission.IntakeAgentFrontdesk,
};

export const useCockpitPanel = () => {
  const [textFilter, setTextFilter] = useState('');
  const { hasPermission } = usePermissions();

  const availableThreadCategories = useMemo(
    () =>
      pipe<[CategoryPermissions], CategoryPermissions, SvbrgCategoryType[]>(
        pickBy((permission) => hasPermission(permission)),
        keys,
      )(threadCategoryPermissionMap),
    [hasPermission],
  );

  const [activeFilters, setActiveFilters] = useState<SvbrgCategoryType[]>(availableThreadCategories);

  // Debouncing the text filter to reduce number of queries send to 7bridges for filtering
  const debouncedTextFilter = useDebounce<string>(textFilter, 300);

  const toggleFilter = useCallback((filter: SvbrgCategoryType) => {
    setActiveFilters((prev) => {
      const isActive = prev.includes(filter);
      return isActive ? prev.filter((f) => f !== filter) : [...prev, filter];
    });
  }, []);

  const handleTextFilterChange = useCallback((value: string | string[] | null) => {
    if (Array.isArray(value)) {
      setTextFilter(value.join(''));
    } else {
      setTextFilter(value ?? '');
    }
  }, []);

  return {
    textFilter,
    debouncedTextFilter,
    setTextFilter: handleTextFilterChange,
    options: availableThreadCategories,
    activeFilters,
    toggleFilter,
  };
};
